import {
  Button,
  Card,
  DatePicker,
  Grid,
  ImageUpload,
  InputAreaField,
  InputField,
  PLACE_OF_EMPLOYMENT,
  Radio,
  RadioGroup,
  SelectInput,
  scrollToFirstErrorField,
  scrollToSelectedElement,
  theme,
} from "@project/common"
import { Flex, notification } from "antd"
import dayjs, { Dayjs } from "dayjs"
import { useFormik } from "formik"
import { useRouter } from "next/router"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useMutation, useQuery } from "react-query"
import styled from "styled-components"
import {
  METHOD_OF_CONTACT,
  PAGE_SIZE,
  STATUS_OF_USER_VALUES,
} from "../../../constants"
import {
  createUserWorkInformationStatus,
  getAllUserWorkInformationStatus,
} from "../../../services/employmentCertificate.services"
import {
  EmploymentCertificateStatusSchema,
  UserWorkInformationStatusRequestType,
} from "../../../types"
import { EmploymentCertificateStatusList } from "./EmploymentCertificateStatusList"
interface IProps {
  user_id?: string | number
  refetchBasicInfo?: () => void
}

const ImageWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;

  .preview-button {
    position: absolute;
    right: 0;
    @media screen and (max-width: 540px) {
      bottom: 0;
      left: 0;
      margin-left: -15px !important;
    }
    button {
      border: none;
      background: none !important;
      color: #0782c8;
    }
  }
  .image-upload {
    @media screen and (max-width: 540px) {
      margin-bottom: 20px !important;
    }
  }
`
const PlaceOfEmploymentWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  .ant-radio-group {
    display: flex;
    align-items: center;
  }
  .input-field {
    width: auto;
  }
  .date-picker {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`
const ContractMethodStyle = styled.div`
  display: flex;
  gap: 15px;
`
const CertificateError = styled.div`
  color: ${theme.colors.alert};
  font-size: 13px;
`
export const EmploymentCertificateStatus: React.FC<IProps> = ({
  user_id,
  refetchBasicInfo,
}): JSX.Element => {
  const { t } = useTranslation()
  const [page, setPage] = useState<any>()
  const contentRef = useRef<HTMLDivElement>(null)
  const [isFileSubmitting, setIsFileSubmitting] = useState(false)
  const router = useRouter()
  //fetch work status information
  const {
    data: response,
    refetch,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["user-work-status-information", page],
    queryFn: () =>
      getAllUserWorkInformationStatus({
        page,
        pageSize: PAGE_SIZE,
        user_id: +user_id,
      }),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    select: (response) => {
      let previousEmploymentDate,
        previousRetirementDate,
        previousEmploymentPlace,
        previousRemarks = null
      const newData = response?.data?.map((d) => {
        // if no change then copy employment, retirement dates from earlier record
        if (d?.place_of_employment === 1) {
          d.employment_place_date = previousEmploymentDate
          d.employment_retired_date = previousRetirementDate
          d.employment_place = previousEmploymentPlace
          d.remarks = d.remarks || previousRemarks
        }
        previousEmploymentDate = d.employment_place_date
        previousRetirementDate = d.employment_retired_date
        previousEmploymentPlace = d.employment_place
        previousRemarks = d.remarks
        return d
      })
      return {
        ...response,
        data: newData,
      }
    },
  })

  //create status
  const { mutate, isLoading: isSaving } = useMutation({
    mutationFn: createUserWorkInformationStatus,
    onSuccess: () => {
      notification.success({
        message: t("Created Successfully"),
      })
      refetchBasicInfo()
      refetch()
      resetForm()
      scrollToSelectedElement(contentRef)
    },
    onError: () => {
      notification.error({
        message: t("Something went wrong. Please contact administrator"),
      })
    },
  })
  const previousData = (previousRecord, currentRecord) => {
    const retiredDate = dayjs(previousRecord?.employment_retired_date)
    const employmentDate = dayjs(currentRecord?.employment_place_date)
    const diff = employmentDate.diff(retiredDate, "days", true)
    if (diff <= 31) {
      return {
        status: STATUS_OF_USER_VALUES.WORKING_WITHING_ONE_MONTH,
      }
    }
    return {
      status: STATUS_OF_USER_VALUES.WORKING_WITH_MORE_THAN_ONE_MONTH,
    }
  }
  const initialValues: UserWorkInformationStatusRequestType = {
    user_id: +user_id,
    contacted_date: null,
    remarks: "",
    method_of_contact: null,
    employment_certificate: null,
    employment_place: "",
    place_of_employment: 1,
    employment_place_date: null,
    employment_retired_date: null,
    status_of_user: 0,
    method_of_contact_other: "",
  }
  const { values, handleSubmit, setFieldValue, errors, resetForm, ...formik } =
    useFormik({
      initialValues: initialValues,
      validationSchema: EmploymentCertificateStatusSchema,
      onSubmit: (formValues) => {
        let status_of_user = 0
        if (
          formValues?.place_of_employment === 3 &&
          formValues?.employment_retired_date
        ) {
          status_of_user = STATUS_OF_USER_VALUES.NOT_WORKING
        } else if (
          formValues?.place_of_employment === 1 &&
          response?.data?.slice(-1)?.length
        ) {
          status_of_user = response?.data?.slice(-1)?.[0]?.status_of_user || ""
        } else {
          status_of_user = previousData(
            response?.data?.slice(-1)?.[0],
            formValues,
          )?.status
        }
        mutate({
          ...formValues,
          status_of_user,
        })
      },
    })

  useEffect(() => {
    if (formik.submitCount == 0) return
    if (formik.isValid) return
    scrollToFirstErrorField(errors)
  }, [formik.submitCount, formik.isValid])
  // create work  status information
  const filterPage = user_id ? "edit" : "add"
  const handlePaginationChange = (page: number) => {
    setPage(page || 1)
    scrollToSelectedElement(contentRef)
    router.push(
      {
        pathname: `employment-certificate/${filterPage}`,
        query: {
          page,
        },
      },
      undefined,
      { scroll: false },
    )
  }

  const checkForNewStatusDate = (date: Dayjs) => {
    const lastUserStatus = response?.data?.[response?.data?.length - 1] || {}
    if (lastUserStatus?.employment_retired_date) {
      return date.isBefore(dayjs(lastUserStatus?.employment_retired_date))
    } else if (lastUserStatus?.employment_place_date) {
      return date.isBefore(dayjs(lastUserStatus?.employment_place_date))
    }
    return false
  }
  return (
    <Card title={t("Confirm the status of employment")}>
      <Flex component={"form"} vertical gap={16}>
        <div>
          <Grid
            background
            labelContent={t("Contacted date")}
            padding={"15px"}
            required
            labelSpan={6}
          >
            <DatePicker
              name={"contacted_date"}
              placeholder={t("Please select")}
              date={
                values?.contacted_date ? dayjs(values?.contacted_date) : null
              }
              onDateChange={(date: Dayjs) =>
                setFieldValue(
                  "contacted_date",
                  dayjs(date).format("YYYY-MM-DD"),
                )
              }
              error={
                !!formik?.touched?.contacted_date && !!errors?.contacted_date
                  ? errors?.contacted_date
                  : null
              }
            />
          </Grid>
          <Grid
            background
            labelContent={t("Upload certificate of employment")}
            padding={"15px"}
            labelSpan={6}
            textAlign={"left"}
          >
            <ImageWrapper>
              <div className={"image-upload"}>
                <ImageUpload
                  onChange={(data) =>
                    setFieldValue("employment_certificate", data)
                  }
                  isLoading={setIsFileSubmitting}
                  shape={"round"}
                  otherType={"draft"}
                  iconType={"upload"}
                  btnText={t("upload")}
                  btnDefault={false}
                  uploadHint={t("(PDFのみ)")}
                  currentImage={values?.employment_certificate}
                  isImg={false}
                  acceptType={".pdf"}
                />
                <CertificateError>
                  {!!formik?.touched?.employment_certificate &&
                  !!errors?.employment_certificate
                    ? errors?.employment_certificate
                    : null}
                </CertificateError>
              </div>
              {values?.employment_certificate && (
                <div className={"preview-button"}>
                  <Button
                    btnText={t("Preview  Certificate")}
                    onClick={() =>
                      window.open(values?.employment_certificate, "_blank")
                    }
                  />
                </div>
              )}
            </ImageWrapper>
          </Grid>
          <Grid
            background
            labelContent={t("Place of employment")}
            padding={"15px"}
            labelSpan={6}
            required
          >
            <PlaceOfEmploymentWrapper>
              <RadioGroup
                name={"place_of_employment"}
                value={values?.place_of_employment || null}
                options={PLACE_OF_EMPLOYMENT || []}
                onChange={({ target: { value } }) => {
                  setFieldValue("place_of_employment", value)
                  setFieldValue("employment_retired_date", null)
                  const empDate = `${
                    values?.employment_place_date
                      ? dayjs(values?.employment_place_date).isValid()
                        ? `${t("Employment date")}: ${dayjs(
                            values?.employment_place_date,
                          ).format("YYYY-MM-DD")}\n`
                        : ""
                      : ""
                  }`
                  const retire_date = `${
                    values?.employment_retired_date
                      ? dayjs(values?.employment_retired_date).isValid()
                        ? `${t("Retirement date")}: ${dayjs(
                            values?.employment_retired_date,
                          ).format("YYYY-MM-DD")}\n`
                        : ""
                      : ""
                  }`
                  const updatedVal =
                    value != 3 ? empDate : empDate + retire_date

                  setFieldValue("remarks", updatedVal)
                }}
              />
              {values?.place_of_employment === 2 && (
                <>
                  <div className={"input-field"}>
                    <InputField
                      name={"employment_place"}
                      value={values?.employment_place}
                      onChange={(val) =>
                        setFieldValue("employment_place", val.target.value)
                      }
                      placeholder={t("Please enter")}
                      size={"large"}
                      width={"224px"}
                      error={
                        formik?.touched?.employment_place &&
                        errors?.employment_place
                          ? errors?.employment_place
                          : null
                      }
                    />
                  </div>
                  <div className={"date-picker"}>
                    <span className={"title"}>{t("Employment date")}</span>
                    <DatePicker
                      name={"employment_place_date"}
                      placeholder={t("Please select")}
                      disabledDate={(date: Dayjs) =>
                        checkForNewStatusDate(date)
                      }
                      date={
                        values?.employment_place_date
                          ? dayjs(values?.employment_place_date)
                          : null
                      }
                      onDateChange={(date: Dayjs) => {
                        setFieldValue(
                          "employment_place_date",
                          dayjs(date).isValid()
                            ? dayjs(date).format("YYYY-MM-DD")
                            : null,
                        )
                        const empDate = `${
                          dayjs(date).isValid()
                            ? `${t("Employment date")}: ${dayjs(date)?.format(
                                "YYYY年MM月DD日",
                              )}\n`
                            : ""
                        }`
                        const retire_date = `${
                          values?.employment_retired_date
                            ? dayjs(values?.employment_retired_date).isValid()
                              ? `${t("Retirement date")}: ${dayjs(
                                  values?.employment_retired_date,
                                )?.format("YYYY年MM月DD日")}\n`
                              : ""
                            : ""
                        }`
                        const updatedVal =
                          values?.place_of_employment != 3
                            ? empDate
                            : empDate + retire_date

                        setFieldValue("remarks", updatedVal)
                      }}
                      error={
                        !!formik?.touched?.employment_place_date &&
                        !!errors?.employment_place_date
                          ? errors?.employment_place_date
                          : null
                      }
                    />
                  </div>
                </>
              )}

              <div className={"date-picker"}>
                <Flex justify={"center"}>
                  <Radio
                    label={t("Register Retirement")}
                    value={values?.place_of_employment || null}
                    checked={values?.place_of_employment == 3}
                    onChange={() => {
                      setFieldValue("place_of_employment", 3).then(() => {
                        setFieldValue("employment_place", null)
                        setFieldValue("employment_place_date", null)
                      })
                    }}
                  />
                </Flex>
                {values?.place_of_employment === 3 && (
                  <>
                    <span className={"title"}>{t("Retired date")}</span>
                    <DatePicker
                      name={"employment_retired_date"}
                      placeholder={t("Please select")}
                      disabledDate={(date: Dayjs) =>
                        checkForNewStatusDate(date)
                      }
                      date={
                        values?.employment_retired_date
                          ? dayjs(values?.employment_retired_date)
                          : null
                      }
                      onDateChange={(date: Dayjs) => {
                        setFieldValue(
                          "employment_retired_date",
                          dayjs(date).isValid()
                            ? dayjs(date)?.format("YYYY-MM-DD")
                            : null,
                        )

                        const empDate = `${
                          values?.employment_place_date
                            ? dayjs(values?.employment_place_date).isValid()
                              ? `${t("Employment date")}: ${dayjs(
                                  values?.employment_place_date,
                                )?.format("YYYY年MM月DD日")}\n`
                              : ""
                            : ""
                        }`
                        const retire_date = `${
                          dayjs(date).isValid()
                            ? `${t("Retirement date")}: ${dayjs(date)?.format(
                                "YYYY年MM月DD日",
                              )}\n`
                            : ""
                        }`
                        const updatedVal = empDate + retire_date

                        setFieldValue("remarks", updatedVal)
                      }}
                    />
                  </>
                )}
              </div>
            </PlaceOfEmploymentWrapper>
          </Grid>
          <Grid
            background
            labelContent={t("Remarks")}
            padding={"15px"}
            labelSpan={6}
          >
            <InputAreaField
              rows={3}
              resize={false}
              placeholder={t("Please enter")}
              name={"remarks"}
              value={values?.remarks || null}
              onChange={({ target: { value } }) =>
                setFieldValue("remarks", value)
              }
            />
          </Grid>
          <Grid
            background
            labelContent={t("Method of contact")}
            padding={"15px"}
            labelSpan={6}
          >
            <ContractMethodStyle>
              <SelectInput
                name={"employment_date"}
                placeholder={t("Please select")}
                value={values?.method_of_contact || null}
                options={METHOD_OF_CONTACT || []}
                onChange={(val) => {
                  setFieldValue("method_of_contact", val)
                  setFieldValue("method_of_contact_other", "")
                }}
                error={
                  formik?.touched?.method_of_contact &&
                  errors?.method_of_contact
                    ? t(`${errors?.method_of_contact}`)
                    : ""
                }
              />
              {values?.method_of_contact == 3 && (
                <InputField
                  name={"method_of_contact_other"}
                  value={values?.method_of_contact_other}
                  onChange={(val) =>
                    setFieldValue("method_of_contact_other", val.target.value)
                  }
                  placeholder={t("Please enter")}
                  size={"large"}
                  width={"224px"}
                  error={
                    formik?.touched?.method_of_contact_other &&
                    errors?.method_of_contact_other
                      ? t(`${errors?.method_of_contact_other}`)
                      : ""
                  }
                />
              )}
            </ContractMethodStyle>
          </Grid>
        </div>

        <Flex gap={8} wrap={"wrap"}>
          <Button
            btnText={t("Cancel")}
            shape={"round"}
            disabled={isSaving || isLoading}
            onClick={() => router.push("/users")}
          />
          <Button
            btnText={t("Save")}
            shape={"round"}
            type={"primary"}
            htmlType={"submit"}
            disabled={isLoading || isFileSubmitting}
            isLoading={isSaving}
            onClick={handleSubmit}
          />
        </Flex>
      </Flex>
      <br />
      <div ref={contentRef}>
        <EmploymentCertificateStatusList
          dataSource={response?.data || []}
          page={page}
          total={(response?.count as any) || 0}
          isLoading={isLoading || isFetching}
          handlePaginationChange={handlePaginationChange}
          refetch={refetch}
          refetchBasicInfo={refetchBasicInfo}
        />
      </div>
    </Card>
  )
}
